import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import {
    addUser,
    addUsers,
    findUserNamesInUse,
    generateNumberedUserNames,
    generateRandomPin,
    isUsernameInUse,
    MAX_BULK_PLAYER_CREATE,
    normalizeUserName,
    type BulkPlayerDraft,
} from "../../services/admin/UserService.tsx";
import { Users } from "../../models/User.tsx";
import Switch from 'react-switch';
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import "../../styles/admin/UserActivity.css"
import "../../styles/admin/Menu.css"
import { logUserActivity } from "../../services/player/LoginService.tsx";
import { logAgentActivity } from "../../services/admin/AgentActivity.ts";
import { useUser } from "../../context/UserContext.tsx";
import AgentContext from "../../context/admin/Agent.tsx";
import { agentUnderLabel, canNestSubAgents, resolveRootMasterName } from "../../helpers/userHierarchy.ts";
import { DEFAULT_WAGER_LIMITS_PRESET } from "../../services/player/SportService.tsx";

const PLAYER_LIMIT_FIELDS = [
    { key: "creditLimit", label: "Credit limit", example: "2000", group: "Account" },
    { key: "regularBetMaxWager", label: "Regular", example: String(DEFAULT_WAGER_LIMITS_PRESET.regularBetMaxWager), group: "Wager limits" },
    { key: "parlayMaxWager", label: "Parlay", example: String(DEFAULT_WAGER_LIMITS_PRESET.parlayMaxWager), group: "Wager limits" },
    { key: "teaserMaxWager", label: "Teaser", example: String(DEFAULT_WAGER_LIMITS_PRESET.teaserMaxWager), group: "Wager limits" },
    { key: "liveMaxWager", label: "Live", example: String(DEFAULT_WAGER_LIMITS_PRESET.liveMaxWager), group: "Wager limits" },
    { key: "propsMaxWager", label: "Props", example: String(DEFAULT_WAGER_LIMITS_PRESET.propsMaxWager), group: "Wager limits" },
    { key: "contestMaxWager", label: "Future", example: String(DEFAULT_WAGER_LIMITS_PRESET.contestMaxWager), group: "Wager limits" },
    { key: "maxPayout", label: "Max Payout", example: "", placeholder: "Not set", group: "Payout & odds" },
    { key: "maxFreePlayPayout", label: "Max Free Play Win", example: "", placeholder: "Not set", group: "Payout & odds" },
    { key: "maxFreePlayOdds", label: "Max Free Play Odds", example: "", placeholder: "Not set", group: "Payout & odds" },
    { key: "maxOverallOdds", label: "Max Overall Odds", example: "", placeholder: "Not set", group: "Payout & odds" },
] as const;

type PlayerLimitKey = (typeof PLAYER_LIMIT_FIELDS)[number]["key"];

const emptyPlayerLimits = (): Record<PlayerLimitKey, string> =>
    Object.fromEntries(PLAYER_LIMIT_FIELDS.map(field => [field.key, field.example])) as Record<PlayerLimitKey, string>;

const playerLimitsPayload = (values: Record<PlayerLimitKey, string>) => {
    const payload: Partial<Record<PlayerLimitKey, number>> = {};
    PLAYER_LIMIT_FIELDS.forEach(field => {
        const raw = values[field.key].trim();
        if (!raw) return;
        const parsed = Number(raw.replace(/,/g, ""));
        if (Number.isFinite(parsed)) payload[field.key] = parsed;
    });
    return payload;
};

type CreateMode = 'single' | 'bulk';
type BulkSource = 'generate' | 'list';
type BulkRowStatus = 'ok' | 'taken' | 'duplicate';

type ParsedPlayerRow = {
    userName: string;
    pin?: string;
    limits?: Record<PlayerLimitKey, string>;
    duplicateInList: boolean;
};

type BulkReviewRow = ParsedPlayerRow & {
    status: BulkRowStatus;
};

const IMPORT_NAME_HEADERS = new Set(['USERNAME', 'USER', 'PLAYER', 'NAME']);
const IMPORT_PIN_HEADERS = new Set(['PASSWORD', 'PIN']);

const splitImportCells = (line: string) => line.split(/[,;\t]/).map(part => (part ?? '').trim());

const importHeaderMap = (cells: string[]) => {
    const map = new Map<string, number>();
    cells.forEach((cell, index) => {
        const name = cell.trim().toUpperCase();
        if (!name) return;
        if (IMPORT_NAME_HEADERS.has(name)) map.set('userName', index);
        if (IMPORT_PIN_HEADERS.has(name)) map.set('pin', index);
        PLAYER_LIMIT_FIELDS.forEach(field => {
            if (name === field.label.toUpperCase() || name === field.key.toUpperCase()) {
                map.set(field.key, index);
            }
        });
    });
    return map.has('userName') ? map : null;
};

const limitsFromCells = (cells: string[], columns: Map<string, number> | null): Record<PlayerLimitKey, string> => {
    const limits = emptyPlayerLimits();
    PLAYER_LIMIT_FIELDS.forEach((field, index) => {
        const mappedIndex = columns?.get(field.key);
        if (columns && mappedIndex === undefined) return;
        const cellIndex = mappedIndex ?? index + 2;
        const raw = (cells[cellIndex] ?? '').replace(/[$,]/g, '').trim();
        if (!raw || raw.toUpperCase() === 'NOT SET') return;
        if (Number.isFinite(Number(raw))) limits[field.key] = raw;
    });
    return limits;
};

const parsePlayerRows = (text: string): ParsedPlayerRow[] => {
    const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    const seen = new Set<string>();
    const players: ParsedPlayerRow[] = [];
    const headerColumns = lines.length > 0 ? importHeaderMap(splitImportCells(lines[0])) : null;

    lines.forEach((line, lineIndex) => {
        if (lineIndex === 0 && headerColumns) return;
        const cells = splitImportCells(line);
        const rawName = headerColumns ? cells[headerColumns.get('userName') ?? 0] : cells[0];
        const pinIndex = headerColumns?.get('pin');
        const rawPin = headerColumns
            ? (pinIndex === undefined ? '' : cells[pinIndex])
            : cells[1];
        const userName = normalizeUserName(rawName || '');
        if (!userName || IMPORT_NAME_HEADERS.has(userName)) return;

        players.push({
            userName,
            pin: rawPin ? rawPin.toUpperCase() : undefined,
            limits: limitsFromCells(cells, headerColumns),
            duplicateInList: seen.has(userName),
        });
        seen.add(userName);
    });

    return players;
};

const downloadExcelTemplate = () => {
    const headers = ['Username', 'Password', ...PLAYER_LIMIT_FIELDS.map(field => field.label)];
    const example = (userName: string, password: string, limits: Record<PlayerLimitKey, string>) => ([
        userName,
        password,
        ...PLAYER_LIMIT_FIELDS.map(field => limits[field.key]),
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([
        headers,
        example('PLAYER01', '', emptyPlayerLimits()),
        example('PLAYER02', 'AB12C', {
            ...emptyPlayerLimits(),
            creditLimit: '1500',
            regularBetMaxWager: '1000',
            maxPayout: '5000',
        }),
    ]);
    worksheet['!cols'] = headers.map(header => ({ wch: Math.max(16, header.length + 2) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Players');
    XLSX.writeFile(workbook, 'PlayerImportTemplate.xlsx');
};

const RegisterPlayer = () => {

    const { user: currentUser } = useUser();
    const { allAgents } = useContext(AgentContext);
    const [randomPin, setRandomPin] = useState(true);
    const [parentInput, setParentInput] = useState('');
    const [createMode, setCreateMode] = useState<CreateMode>('single');
    const [bulkSource, setBulkSource] = useState<BulkSource>('generate');
    const [bulkPrefix, setBulkPrefix] = useState('');
    const [bulkStart, setBulkStart] = useState(1);
    const [bulkCount, setBulkCount] = useState(10);
    const [bulkPad, setBulkPad] = useState(1);
    const [bulkListText, setBulkListText] = useState('');
    const [bulkSharedPin, setBulkSharedPin] = useState('');
    const [formData, setFormData] = useState<{
        master: string; parent: string; userName: string; pin: string; selectedAgent: string | null;
    }>({
        master: '', parent: '', userName: '', pin: '', selectedAgent: null
    });
    const [createdUserName, setCreatedUserName] = useState<string | null>(null);
    const [createdPlayers, setCreatedPlayers] = useState<Array<{ userName: string; pin: string }>>([]);
    const [skippedPlayers, setSkippedPlayers] = useState<Array<{ userName: string; reason: string }>>([]);
    const [takenNames, setTakenNames] = useState<Set<string>>(new Set());
    const [isCheckingNames, setIsCheckingNames] = useState(false);
    const [awaitingConflictChoice, setAwaitingConflictChoice] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [usernameTaken, setUsernameTaken] = useState(false);
    const [playerLimits, setPlayerLimits] = useState(emptyPlayerLimits);
    const [bulkLimits, setBulkLimits] = useState<Record<string, Record<PlayerLimitKey, string>>>({});
    const [rootMaster, setRootMaster] = useState('');
    const modalRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const canAssignToChildAgents = Boolean(
        currentUser && currentUser.userType === 'subAgent' && canNestSubAgents(currentUser)
    );
    const canChooseAgent = currentUser?.userType === 'masterAgent' || canAssignToChildAgents;
    const assignableAgents = useMemo(() => {
        if (!canAssignToChildAgents || !currentUser) return allAgents;
        if (allAgents.some(agent => agent.userName === currentUser.userName)) return allAgents;
        return [currentUser, ...allAgents];
    }, [allAgents, canAssignToChildAgents, currentUser]);

    useEffect(() => {
        const modalEl = modalRef.current;
        if (!modalEl) return;

        const handleHidden = () => {
            setIsLoading(false);
            setIsSuccess(false);
            setErrorMessage(null);
            setAwaitingConflictChoice(false);
        };

        modalEl.addEventListener('hidden.bs.modal', handleHidden);

        return () => {
            modalEl.removeEventListener('hidden.bs.modal', handleHidden);
        };
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        if (name === 'userName') {
            setUsernameTaken(false);
            setFormData(prev => ({
                ...prev,
                userName: value.toUpperCase(),
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleUsernameBlur = async () => {
        const userName = normalizeUserName(formData.userName);
        if (!userName) {
            setUsernameTaken(false);
            return;
        }

        setUsernameTaken(await isUsernameInUse(userName));
    };

    const applySelectedAgent = (selectedAgent: Users) => {
        setFormData(prev => ({
            ...prev,
            parent: selectedAgent.userName,
            master: currentUser?.userType === 'masterAgent'
                ? currentUser.userName
                : (rootMaster || prev.master),
            selectedAgent: selectedAgent.userName,
        }));
        setParentInput(selectedAgent.userName);
    };

    const handleAgentSelect = (userName: string) => {
        if (!userName) {
            setParentInput('');
            setFormData(prev => ({
                ...prev,
                selectedAgent: null,
                parent: currentUser?.userType === 'subAgent' ? currentUser.userName : '',
            }));
            return;
        }
        const selectedAgent = assignableAgents.find(agent => agent.userName === userName);
        if (selectedAgent) applySelectedAgent(selectedAgent);
    };

    const setHierarchyForSubAgent = () => {
        if (currentUser!.userType !== 'subAgent') return;

        setFormData(prevFormData => ({
            ...prevFormData,
            parent: prevFormData.selectedAgent || currentUser!.userName,
            master: rootMaster || currentUser!.parent || '',
        }));
    };

    const resetCreateForm = () => {
        const isSubAgent = currentUser?.userType === 'subAgent';

        setFormData({
            pin: randomPin ? generateRandomPin() : '',
            userName: '',
            selectedAgent: null,
            parent: isSubAgent ? currentUser.userName : '',
            master: isSubAgent ? (rootMaster || currentUser.parent || '') : (currentUser?.userType === 'masterAgent' ? currentUser.userName : ''),
        });
        setParentInput('');
        setUsernameTaken(false);
        setCreatedPlayers([]);
        setSkippedPlayers([]);
        setTakenNames(new Set());
        setAwaitingConflictChoice(false);
        setBulkListText('');
        setBulkSharedPin('');
        setPlayerLimits(emptyPlayerLimits());
        setBulkLimits({});
    };

    useEffect(() => {
        if (!currentUser?.userName) return;
        if (currentUser.userType === 'masterAgent') {
            setRootMaster(currentUser.userName);
            return;
        }
        let cancelled = false;
        resolveRootMasterName(currentUser).then(name => {
            if (!cancelled) setRootMaster(name);
        }).catch(() => {
            if (!cancelled) setRootMaster(currentUser.parent || '');
        });
        return () => {
            cancelled = true;
        };
    }, [currentUser]);

    useEffect(() => {
        setHierarchyForSubAgent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, rootMaster]);

    const parentUser = currentUser?.userType === "masterAgent"
        ? formData.selectedAgent ?? undefined
        : (canAssignToChildAgents ? (formData.selectedAgent || currentUser?.userName) : currentUser?.userName);

    const rejectMissingAgent = () => {
        if (currentUser?.userType === 'masterAgent' && !formData.selectedAgent) {
            setErrorMessage('Please select an agent.');
            return true;
        }
        if (canAssignToChildAgents && parentInput.trim() && !formData.selectedAgent) {
            setErrorMessage('Select an agent from the list, or clear the field to add the player under you.');
            return true;
        }
        if (
            formData.selectedAgent
            && !assignableAgents.some(agent => agent.userName === formData.selectedAgent)
        ) {
            setErrorMessage('Select an agent from your list.');
            return true;
        }
        return false;
    };

    const masterUser = currentUser?.userType === "masterAgent"
        ? currentUser.userName
        : (rootMaster || currentUser?.parent || '');

    const generatedNames = useMemo(() => {
        const count = Math.min(Math.max(0, Number(bulkCount) || 0), MAX_BULK_PLAYER_CREATE);
        if (count === 0) return [];
        return generateNumberedUserNames(bulkPrefix, Number(bulkStart) || 1, count, bulkPad);
    }, [bulkPrefix, bulkStart, bulkCount, bulkPad]);

    const parsedRows = useMemo(() => parsePlayerRows(bulkListText), [bulkListText]);

    useEffect(() => {
        if (bulkSource !== 'list') return;
        setBulkLimits(prev => {
            const next = { ...prev };
            parsedRows.forEach(row => {
                if (row.limits) next[row.userName] = row.limits;
            });
            return next;
        });
    }, [bulkSource, parsedRows]);

    const sourceRows: ParsedPlayerRow[] = useMemo(() => {
        if (bulkSource === 'generate') {
            return generatedNames.map(userName => ({
                userName,
                duplicateInList: false,
            }));
        }
        return parsedRows;
    }, [bulkSource, generatedNames, parsedRows]);

    const reviewRows: BulkReviewRow[] = useMemo(() => (
        sourceRows.map(row => ({
            ...row,
            status: row.duplicateInList
                ? 'duplicate'
                : takenNames.has(row.userName)
                    ? 'taken'
                    : 'ok',
        }))
    ), [sourceRows, takenNames]);

    const uniqueCandidates = useMemo(
        () => sourceRows.filter(row => !row.duplicateInList).slice(0, MAX_BULK_PLAYER_CREATE),
        [sourceRows]
    );

    const limitsForPlayer = (userName: string) => bulkLimits[userName] ?? emptyPlayerLimits();

    const bulkDrafts: BulkPlayerDraft[] = useMemo(() => (
        uniqueCandidates.map(player => ({
            userName: player.userName,
            pin: player.pin || '',
            parent: parentUser,
            master: masterUser,
            ...playerLimitsPayload(bulkLimits[player.userName] ?? emptyPlayerLimits()),
        }))
    ), [uniqueCandidates, parentUser, masterUser, bulkLimits]);

    const validDrafts = useMemo(
        () => bulkDrafts.filter(player => !takenNames.has(player.userName)),
        [bulkDrafts, takenNames]
    );

    const displayRows = useMemo(() => (
        [...reviewRows].sort((left, right) => {
            if (left.status === 'ok' && right.status !== 'ok') return 1;
            if (left.status !== 'ok' && right.status === 'ok') return -1;
            return 0;
        })
    ), [reviewRows]);
    const conflictRows = reviewRows.filter(row => row.status !== 'ok');
    const takenUserNames = [...new Set(reviewRows.filter(row => row.status === 'taken').map(row => row.userName))];
    const duplicateUserNames = [...new Set(reviewRows.filter(row => row.status === 'duplicate').map(row => row.userName))];

    const candidateNamesKey = uniqueCandidates.map(row => row.userName).join('\n');

    useEffect(() => {
        if (createMode !== 'bulk') {
            setTakenNames(new Set());
            setIsCheckingNames(false);
            return;
        }

        if (!candidateNamesKey) {
            setTakenNames(new Set());
            setIsCheckingNames(false);
            return;
        }

        const names = candidateNamesKey.split('\n').filter(Boolean);
        let cancelled = false;
        setIsCheckingNames(true);
        setAwaitingConflictChoice(false);

        const timer = window.setTimeout(async () => {
            try {
                const used = await findUserNamesInUse(names);
                if (!cancelled) setTakenNames(used);
            } catch {
                if (!cancelled) setTakenNames(new Set());
            } finally {
                if (!cancelled) setIsCheckingNames(false);
            }
        }, 400);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [createMode, candidateNamesKey]);

    const submitBulkPlayers = async (createValidOnly: boolean) => {
        if (rejectMissingAgent()) return;
        if (bulkSource === 'generate' && !normalizeUserName(bulkPrefix)) {
            setErrorMessage('Enter a prefix for the player usernames.');
            return;
        }
        if (bulkDrafts.length === 0) {
            setErrorMessage(bulkSource === 'generate'
                ? 'Enter a prefix and how many players to create.'
                : 'Paste or upload at least one username.');
            return;
        }

        setIsLoading(true);
        setIsSuccess(false);
        setErrorMessage(null);

        try {
            const used = await findUserNamesInUse(bulkDrafts.map(player => player.userName));
            setTakenNames(used);
            setIsCheckingNames(false);

            const availableDrafts = bulkDrafts.filter(player => !used.has(player.userName));
            const blockedNames = bulkDrafts
                .filter(player => used.has(player.userName))
                .map(player => player.userName);

            if (!createValidOnly && blockedNames.length > 0) {
                setAwaitingConflictChoice(true);
                setIsLoading(false);
                return;
            }

            const draftsToCreate = availableDrafts.map(player => ({
                ...player,
                pin: player.pin || (randomPin ? generateRandomPin() : bulkSharedPin.toUpperCase()),
            }));

            if (draftsToCreate.length === 0) {
                setAwaitingConflictChoice(true);
                setErrorMessage('None of these usernames can be created. Change the names marked in red.');
                setIsLoading(false);
                return;
            }

            if (draftsToCreate.some(player => !player.pin)) {
                setErrorMessage('Enter a password for these players, or turn on random passwords.');
                setIsLoading(false);
                return;
            }

            setAwaitingConflictChoice(false);
            const result = await addUsers(draftsToCreate);
            setCreatedPlayers(result.created);
            setSkippedPlayers(result.skipped);
            setCreatedUserName(result.created[0]?.userName ?? null);

            if (result.error) {
                setErrorMessage(result.error);
                if (result.created.length > 0) setIsSuccess(true);
                return;
            }

            if (result.created.length === 0) {
                setErrorMessage(result.skipped.length > 0
                    ? 'No players were created. Usernames are already in use or invalid.'
                    : 'No players were created.');
                return;
            }

            setIsSuccess(true);
            if (currentUser?.userName && result.created.length > 0) {
                void logAgentActivity({
                    userName: currentUser.userName,
                    parent: currentUser.parent,
                    activity: 'Create players',
                    detail: result.created.map(player => player.userName).join(', '),
                });
            }
        } catch (error: any) {
            setErrorMessage(error?.message || 'Error creating players. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (createMode === 'bulk') {
            await submitBulkPlayers(false);
            return;
        }

        if (rejectMissingAgent()) return;

        const upperUserName = normalizeUserName(formData.userName);

        if (!upperUserName) {
            setErrorMessage('Please enter a player username.');
            return;
        }

        setIsLoading(true);
        setIsSuccess(false);
        setErrorMessage(null);

        if (await isUsernameInUse(upperUserName)) {
            setUsernameTaken(true);
            setErrorMessage('Username is already in use');
            setIsLoading(false);
            return;
        }

        const upperPin = formData.pin.toUpperCase();
        setCreatedUserName(upperUserName);
        setCreatedPlayers([{ userName: upperUserName, pin: upperPin }]);
        setSkippedPlayers([]);

        const newUser = {
            userName: upperUserName,
            pin: upperPin,
            parent: parentUser,
            master: masterUser,
            userType: 'Player',
            ...playerLimitsPayload(playerLimits),
        };

        try {
            await addUser(newUser);
            if (currentUser?.userName) {
                void logAgentActivity({
                    userName: currentUser.userName,
                    parent: currentUser.parent,
                    activity: 'Create player',
                    customer: upperUserName,
                });
            }

            setIsSuccess(true);
            setUsernameTaken(false);
            resetCreateForm();
        } catch (error: any) {
            setErrorMessage(
                error?.message || 'Error creating player. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (randomPin) {
            setFormData(prev => ({
                ...prev,
                pin: generateRandomPin()
            }));
        }
    }, [randomPin]);

    const navigate = useNavigate();

    const handleAddAnother = () => {
        resetCreateForm();
        setIsSuccess(false);
        setErrorMessage(null);
        setIsLoading(false);
    };

    const handleDone = () => {
        if (createdPlayers.length > 1) {
            logUserActivity(currentUser!.userName, "View Customer Admin");
            navigate('/customerAdmin');
            return;
        }

        logUserActivity(currentUser!.userName, `View User Basic: ${createdUserName}`);
        navigate(`/user/${createdUserName}`, {
            state: { from: "PlayerList" }
        });
    };

    const handleListFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            if (/\.(xlsx|xls|csv)$/i.test(file.name)) {
                const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json<(string | number)[]>(sheet, { header: 1 });
                const text = rows
                    .map(row => (row ?? []).map(value => value ?? '').join(','))
                    .filter(line => line.replace(/,/g, '').trim() !== '')
                    .join('\n');
                setBulkListText(text);
                setBulkSource('list');
            } else {
                setBulkListText(await file.text());
                setBulkSource('list');
            }
        } catch {
            setErrorMessage('Could not read that file. Use a text, CSV, or Excel list.');
        } finally {
            event.target.value = '';
        }
    };

    const createButtonCount = createMode === 'bulk'
        ? (takenNames.size > 0 || duplicateUserNames.length > 0 ? validDrafts.length : bulkDrafts.length)
        : 0;
    const showConflictChoice = createMode === 'bulk'
        && !isSuccess
        && !isLoading
        && (awaitingConflictChoice || conflictRows.length > 0);

    return (
        <div>
            <div className="mt-2 container-fluid add-player-page">
                <div className="col container-title-addCustomer align-items-center">
                    <span>Add Player</span>
                    <button className="button-agent" data-bs-toggle="modal" data-bs-target="#addCustomerModal">
                        {createMode === 'bulk' && createButtonCount > 1 ? `Create ${createButtonCount}` : 'Create'}
                    </button>
                </div>
                <div className="opcion-add">
                    <div className="add-player-mode">
                        <button
                            type="button"
                            className={`add-player-mode-btn${createMode === 'single' ? ' active' : ''}`}
                            onClick={() => setCreateMode('single')}
                        >
                            One player
                        </button>
                        <button
                            type="button"
                            className={`add-player-mode-btn${createMode === 'bulk' ? ' active' : ''}`}
                            onClick={() => setCreateMode('bulk')}
                        >
                            Many players
                        </button>
                    </div>
                    <div className="add-player-body">
                    <div className="row">
                        {canChooseAgent && (
                            <div className="col-lg-3 col-md-3">
                                <span>{canAssignToChildAgents ? 'Under which agent?' : 'Under which SubAgent?'}</span>
                                <select
                                    className="form-control input-agent add-player-agent-select"
                                    value={formData.selectedAgent ?? ''}
                                    onChange={event => handleAgentSelect(event.target.value)}
                                >
                                    <option value="">
                                        {canAssignToChildAgents ? 'Under you' : 'Select an agent'}
                                    </option>
                                    {assignableAgents
                                        .slice()
                                        .sort((a, b) => a.userName.localeCompare(b.userName))
                                        .map(agent => {
                                            const under = agentUnderLabel(agent, currentUser, allAgents);
                                            return (
                                                <option key={agent.id ?? agent.userName} value={agent.userName}>
                                                    {under ? `${agent.userName} · ${under}` : agent.userName}
                                                </option>
                                            );
                                        })}
                                </select>
                            </div>
                        )}
                        {createMode === 'single' && (
                            <div className="col-lg-3 col-md-3 mb-3 add-player-username">
                                <span>Username</span>
                                <input
                                    type="text"
                                    className="form-control input-agent"
                                    name="userName"
                                    value={formData.userName}
                                    onChange={handleInputChange}
                                    onBlur={handleUsernameBlur}
                                    autoComplete="off"
                                    placeholder="Enter a unique name"
                                    required
                                />
                                {usernameTaken && (
                                    <small className="text-danger">This username is already in use.</small>
                                )}
                            </div>
                        )}
                        <div className="col-lg-4 col-md-4 mb-3 random-password-field">
                            <span className="random-password-label">Password</span>
                            <div className="center-switch">
                                <Switch id='active' name="pin" checked={randomPin}
                                    onChange={() => {
                                        const newValue = !randomPin;
                                        setRandomPin(newValue);
                                        setFormData(prev => ({
                                            ...prev,
                                            pin: newValue ? generateRandomPin() : ''
                                        }));
                                    }}
                                    offColor="#ff6347"
                                    onColor="#4caf50"
                                    offHandleColor="#ffffff"
                                    onHandleColor="#ffffff"
                                />
                                <span>Generate random password</span>
                            </div>
                        </div>
                    </div>

                    {createMode === 'single' && (
                    <div className="add-player-limits">
                        <span className="add-player-limits__title">Limits</span>
                        <small>The boxes start with the system defaults. If a limit or a max win is left empty, that player uses the system default.</small>
                        {["Account", "Wager limits", "Payout & odds"].map(group => (
                            <div key={group} className="add-player-limits__group">
                                <span>{group}</span>
                                <div className="row">
                                    {PLAYER_LIMIT_FIELDS.filter(field => field.group === group).map(field => (
                                        <div key={field.key} className="col-lg-3 col-md-4 col-6 mb-3">
                                            <span>{field.label}</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                className="form-control input-agent"
                                                value={playerLimits[field.key]}
                                                placeholder={"placeholder" in field ? field.placeholder : field.example}
                                                onChange={event => {
                                                    const next = event.target.value.replace(/[^0-9.]/g, "");
                                                    setPlayerLimits(prev => ({ ...prev, [field.key]: next }));
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    )}

                    {createMode === 'bulk' && (
                        <div className="bulk-player-panel">
                            <div className="add-player-mode add-player-mode--soft">
                                <button
                                    type="button"
                                    className={`add-player-mode-btn${bulkSource === 'generate' ? ' active' : ''}`}
                                    onClick={() => setBulkSource('generate')}
                                >
                                    Numbered names
                                </button>
                                <button
                                    type="button"
                                    className={`add-player-mode-btn${bulkSource === 'list' ? ' active' : ''}`}
                                    onClick={() => setBulkSource('list')}
                                >
                                    Paste / upload list
                                </button>
                            </div>

                            {bulkSource === 'generate' ? (
                                <div className="row add-player-generate">
                                    <div className="col-6 col-lg-3 col-md-4 mb-3">
                                        <span>Prefix</span>
                                        <input
                                            type="text"
                                            className="form-control input-agent"
                                            value={bulkPrefix}
                                            onChange={event => setBulkPrefix(event.target.value.toUpperCase())}
                                            placeholder="JOCK"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="col-6 col-lg-2 col-md-4 mb-3">
                                        <span>Start number</span>
                                        <input
                                            type="number"
                                            min={0}
                                            className="form-control input-agent"
                                            value={bulkStart}
                                            onChange={event => setBulkStart(Number(event.target.value))}
                                        />
                                    </div>
                                    <div className="col-6 col-lg-2 col-md-4 mb-3">
                                        <span>How many</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={MAX_BULK_PLAYER_CREATE}
                                            className="form-control input-agent"
                                            value={bulkCount}
                                            onChange={event => setBulkCount(Number(event.target.value))}
                                        />
                                    </div>
                                    <div className="col-6 col-lg-2 col-md-4 mb-3">
                                        <span>Digits</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={8}
                                            className="form-control input-agent"
                                            value={bulkPad}
                                            onChange={event => {
                                                const digits = Math.floor(Number(event.target.value));
                                                setBulkPad(Number.isFinite(digits) ? Math.min(8, Math.max(1, digits)) : 1);
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <div className="bulk-excel-help">
                                        <strong>Excel / CSV format</strong>
                                        <p>
                                            One player per row, max {MAX_BULK_PLAYER_CREATE}. Download the template and keep its first row.
                                            Columns: <code>Username</code> (required), <code>Password</code> (optional), {PLAYER_LIMIT_FIELDS.map(field => field.label).join(', ')}.
                                        </p>
                                        <p><strong>Do this</strong></p>
                                        <ul>
                                            <li>Keep the header row and use those exact names. Each column is then matched by name, not by its place in the file.</li>
                                            <li>Put a number only in the limits you want to change. After upload, check each player card before creating. You can still edit a value there.</li>
                                            <li>Turn on random passwords if you leave Password empty.</li>
                                        </ul>
                                        <p><strong>If you do this</strong></p>
                                        <ul>
                                            <li>A limit or max-win cell is empty, says Not set, or is not a number: only that field uses the system default.</li>
                                            <li>A column is missing or its header is misspelled, and the header row is still there: only that missing field uses the system default. A value does not move into the column next to it. Every other column keeps the number you typed under its own name.</li>
                                            <li>Password is missing or empty, and random passwords are on: a random password is created. If random passwords are off, that player is not created until you enter one.</li>
                                            <li>You delete the header row: the file is no longer read by column name. It is read left to right. If you skip one column, every number to the right of it lands in the wrong field. Keep the header row.</li>
                                            <li>Username is empty or already in use: that row is skipped. A repeated username in the same file is marked as a duplicate.</li>
                                        </ul>
                                        <p>Paste uses the same columns, separated by commas:</p>
                                        <p><code>Username,Password,Credit limit,Regular,Parlay,Teaser,Live,Props,Future,Max Payout,Max Free Play Win,Max Free Play Odds,Max Overall Odds</code></p>
                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={downloadExcelTemplate}>
                                            Download Excel template
                                        </button>
                                    </div>
                                    <span>One player per line: Username, Password, then each limit</span>
                                    <textarea
                                        className="form-control bulk-list-textarea"
                                        value={bulkListText}
                                        onChange={event => setBulkListText(event.target.value)}
                                        placeholder={"Username,Password,Credit limit,Regular,Parlay,Teaser,Live,Props,Future,Max Payout,Max Free Play Win,Max Free Play Odds,Max Overall Odds\nPLAYER01,\nPLAYER02,AB12C,1500,1000,1000,400,2000,2000,500,5000,,,"}
                                    />
                                    <div className="bulk-file-row">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Upload CSV / Excel
                                        </button>
                                        <small>Up to {MAX_BULK_PLAYER_CREATE} players per batch.</small>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".txt,.csv,.xlsx,.xls"
                                            hidden
                                            onChange={handleListFile}
                                        />
                                    </div>
                                </div>
                            )}

                            {!randomPin && (
                                <div className="col-lg-3 col-md-4 mb-3 px-0">
                                    <span>Password for all players</span>
                                    <input
                                        type="text"
                                        className="form-control input-agent"
                                        value={bulkSharedPin}
                                        onChange={event => setBulkSharedPin(event.target.value.toUpperCase())}
                                        autoComplete="off"
                                    />
                                </div>
                            )}

                            <div className="bulk-preview-box">
                                <div>
                                    <strong>{validDrafts.length}</strong> player{validDrafts.length === 1 ? '' : 's'} ready
                                    {takenUserNames.length > 0 && (
                                        <span className="bulk-taken-count"> · {takenUserNames.length} already in use</span>
                                    )}
                                    {duplicateUserNames.length > 0 && (
                                        <span className="bulk-taken-count"> · {duplicateUserNames.length} duplicated in this list</span>
                                    )}
                                    {isCheckingNames && <span className="bulk-checking"> · checking usernames…</span>}
                                </div>
                                {reviewRows.length > 0 && (
                                    <div className="bulk-review-list">
                                        {displayRows.map((row, index) => (
                                            <div
                                                key={`${row.userName}-${index}`}
                                                className={`bulk-review-row${row.status !== 'ok' ? ' is-blocked' : ''}${row.status === 'ok' ? ' bulk-review-row--limits' : ''}`}
                                            >
                                                <div className="bulk-review-row__head">
                                                    <span className="bulk-review-name">{row.userName}</span>
                                                    {row.status === 'taken' && <span>already in use</span>}
                                                    {row.status === 'duplicate' && <span>duplicate in this list</span>}
                                                </div>
                                                {row.status === 'ok' && (
                                                    <div className="row">
                                                        {PLAYER_LIMIT_FIELDS.map(field => (
                                                            <div key={field.key} className="col-6 mb-2">
                                                                <span>{field.label}</span>
                                                                <input
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    className="form-control input-agent bulk-limit-input"
                                                                    value={limitsForPlayer(row.userName)[field.key]}
                                                                    placeholder={"placeholder" in field ? field.placeholder : field.example}
                                                                    onChange={event => {
                                                                        const next = event.target.value.replace(/[^0-9.]/g, "");
                                                                        setBulkLimits(prev => ({
                                                                            ...prev,
                                                                            [row.userName]: {
                                                                                ...(prev[row.userName] ?? emptyPlayerLimits()),
                                                                                [field.key]: next,
                                                                            },
                                                                        }));
                                                                    }}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>

            <div className="modal fade" id="addCustomerModal" tabIndex={-1} ref={modalRef}>
                <div className="modal-dialog ">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {isLoading
                                    ? (createMode === 'bulk' ? 'Creating Players...' : 'Creating Player...')
                                    : isSuccess
                                        ? 'Success'
                                        : showConflictChoice
                                            ? 'Usernames already in use'
                                            : createMode === 'bulk'
                                                ? `Create ${validDrafts.length || bulkDrafts.length || 0} players?`
                                                : 'Are you sure to add a Player?'}
                            </h5>

                            {!isLoading && (
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                />
                            )}
                        </div>

                        <div className="modal-body text-center">

                            {errorMessage && !isLoading && (
                                <div className="alert alert-danger text-start">
                                    <strong>Error:</strong> {errorMessage}
                                </div>
                            )}

                            {isLoading && (
                                <>
                                    <div className="spinner-border text-primary mb-3" />
                                    <p>
                                        {createMode === 'bulk'
                                            ? `Creating ${validDrafts.length || bulkDrafts.length} players, please wait...`
                                            : 'Creating player, please wait...'}
                                    </p>
                                </>
                            )}

                            {isSuccess && !isLoading && (
                                <>
                                    <div className="text-success fs-1 mb-3">✔</div>
                                    <p>
                                        {createMode === 'bulk'
                                            ? `${createdPlayers.length} player${createdPlayers.length === 1 ? '' : 's'} created.`
                                            : 'Player was created successfully.'}
                                    </p>
                                    {skippedPlayers.length > 0 && (
                                        <div className="alert alert-warning text-start mb-0">
                                            <strong>{skippedPlayers.length} skipped:</strong>
                                            <div className="bulk-skipped-list">
                                                {skippedPlayers.slice(0, 8).map(item => (
                                                    <div key={item.userName}>{item.userName}: {item.reason}</div>
                                                ))}
                                                {skippedPlayers.length > 8 && <div>… +{skippedPlayers.length - 8} more</div>}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {!isLoading && !isSuccess && showConflictChoice && (
                                <div className="text-start">
                                    <p>
                                        These usernames are already in use or duplicated. They are marked with a red line.
                                        You can close this window to change them, or create only the names that are available.
                                    </p>
                                    <div className="bulk-review-list bulk-review-list-modal">
                                        {conflictRows.map((row, index) => (
                                            <div key={`${row.userName}-${index}`} className="bulk-review-row is-blocked">
                                                <span className="bulk-review-name">{row.userName}</span>
                                                <span>{row.status === 'taken' ? 'already in use' : 'duplicate in this list'}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {validDrafts.length > 0 && (
                                        <p className="mb-0 mt-3">
                                            <strong>{validDrafts.length}</strong> player{validDrafts.length === 1 ? '' : 's'} can still be created.
                                        </p>
                                    )}
                                </div>
                            )}

                            {!isLoading && !isSuccess && !errorMessage && !showConflictChoice && (
                                createMode === 'bulk' ? (
                                    <p>
                                        You are creating <strong>{validDrafts.length || bulkDrafts.length}</strong> players
                                        {parentUser ? <> under <strong>{parentUser}</strong></> : ''}.
                                    </p>
                                ) : (
                                    <p>
                                        You are creating player{' '}
                                        <strong>{normalizeUserName(formData.userName) || '—'}</strong>
                                        {parentUser ? <> under <strong>{parentUser}</strong></> : ' under the selected agent'}.
                                    </p>
                                )
                            )}
                        </div>

                        <div className="button-container-register-player ">

                            {!isLoading && !isSuccess && showConflictChoice && (
                                <>
                                    <button className="btn btn-danger" data-bs-dismiss="modal">
                                        Close to edit
                                    </button>
                                    {validDrafts.length > 0 && (
                                        <button className="btn btn-primary" onClick={() => submitBulkPlayers(true)}>
                                            Create {validDrafts.length} valid player{validDrafts.length === 1 ? '' : 's'}
                                        </button>
                                    )}
                                </>
                            )}

                            {!isLoading && !isSuccess && !showConflictChoice && (
                                <>
                                    <button className="btn btn-danger" data-bs-dismiss="modal">
                                        Close
                                    </button>
                                    <button className="btn btn-primary" onClick={handleSubmit}>
                                        Confirm
                                    </button>
                                </>
                            )}

                            {isSuccess && (
                                <>
                                    <button className="btn btn-secondary" data-bs-dismiss="modal" onClick={handleAddAnother}>
                                        Add Another
                                    </button>
                                    {(createdUserName || createdPlayers.length > 1) && (
                                        <button className="btn btn-success" onClick={handleDone} data-bs-dismiss="modal">
                                            {createdPlayers.length > 1 ? 'Go to Customer Admin' : 'Go to Player'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}
export default RegisterPlayer;
